// RFC 9457 problem details (04 §Errors, ADR-0016 §4, §8). Every non-2xx response is
// `application/problem+json` with this shape; `code` is the machine-readable key and `type` is
// `https://companyos.app/errors/{code}`.
import { z } from "zod";
import { AddonKey, FeatureKey, MaintenanceTier } from "./enums";
import { IsoDate, Uuid } from "./primitives";

export const PROBLEM_TYPE_BASE = "https://companyos.app/errors/";
export const PROBLEM_CONTENT_TYPE = "application/problem+json";

/** Every problem code the slice returns, with its HTTP status and title. */
export const problemCatalog = {
  unauthenticated: { status: 401, title: "Authentication required" },
  forbidden: { status: 403, title: "Forbidden" },
  not_found: { status: 404, title: "Not found" },
  conflict: { status: 409, title: "Conflict" },
  validation_failed: { status: 422, title: "Validation failed" },
  rate_limited: { status: 429, title: "Too many requests" },
  payload_too_large: { status: 413, title: "Payload too large" },
  internal: { status: 500, title: "Internal error" },
  not_ready: { status: 503, title: "Not ready" },
  // HARDEN-mfa: a user who must use two-factor sign-in (an agency operator always; an owner or
  // admin where the company requires it) and has not enrolled reaches only auth, `/v1/me` and
  // their own security settings until they do.
  mfa_enrollment_required: { status: 403, title: "Two-factor sign-in must be set up first" },
  // WF-CONTROL: a step-up: an enrolled person confirms with a current code (wrong or missing).
  mfa_code_required: { status: 403, title: "A current two-factor code is needed" },
  // EXPORT-CORE: an owner confirms who they are again (password) before taking the company out.
  reauth_required: { status: 403, title: "Confirm your password to continue" },
  // EXPORT-CORE: the archive's secret scan found a credential; nothing was handed out.
  export_blocked: { status: 409, title: "The export was stopped by the secret check" },
  // Entitlements (ADR-0016 §4, §8)
  feature_disabled: { status: 403, title: "Feature not enabled" },
  feature_read_only: { status: 403, title: "Feature is read-only" },
  addon_requires: { status: 409, title: "Add-on requires another add-on" },
  addon_required_by: { status: 409, title: "Add-on is required by another add-on" },
  // Idempotency and pagination (04)
  idempotency_key_required: { status: 400, title: "Idempotency-Key required" },
  idempotency_mismatch: { status: 422, title: "Idempotency-Key reused with a different request" },
  invalid_cursor: { status: 422, title: "Invalid cursor" },
  precondition_required: { status: 428, title: "If-Match required" },
  // Keys and ingest (13 §Ingest contract, invariant 3)
  key_unbound: { status: 403, title: "Key is not bound to an active assignment" },
  scope_missing: { status: 403, title: "Key lacks a required scope" },
  kind_not_allowed: { status: 403, title: "Item kind not allowed for this key" },
  actor_not_allowed: { status: 422, title: "Actor fields are not accepted" },
  tz_mismatch: { status: 422, title: "Batch time zone differs from the company's" },
  meter_conflict: { status: 409, title: "Meter identity already posted with different data" },
  // Org (13 §Invariants)
  seat_occupied: { status: 409, title: "Seat already has an active holder of that kind" },
  supervisor_required: {
    status: 422,
    title: "Supervisor required: the reporting chain must end at a person",
  },
  seat_closed: { status: 409, title: "Seat is closed" },
  invitation_invalid: { status: 404, title: "No pending invitation for this token and account" },
  email_unverified: { status: 403, title: "The account has not verified its email" },
  // Org chart, recurring work, templates (M1-02; 04 §Org, 02 §Seat invariants)
  org_chart_cycle: { status: 422, title: "The edges would form a cycle" },
  schedule_invalid: { status: 422, title: "Not a valid RRULE schedule" },
  template_invalid: { status: 422, title: "Template spec or params do not apply" },
  // Chat and work (M2-01; 06)
  edit_window_closed: { status: 409, title: "The message can no longer be edited" },
  status_invalid: { status: 422, title: "Not a status of the task's board" },
  status_in_use: { status: 409, title: "A removed status still has tasks" },
  dependency_blocked: { status: 409, title: "An open hard dependency blocks this status" },
  dependency_cycle: { status: 422, title: "The dependency would form a cycle" },
  // Agents, runs, approvals, finance controls (M3-01; 04 §Auth model, 11 §Controls)
  run_not_owned: { status: 403, title: "The run does not belong to this key's principal" },
  run_state_conflict: { status: 409, title: "The run's state does not allow this" },
  // REACH-S1 (agent-reach §2): the agent does not take asks from the caller; ask for access
  not_in_reach: { status: 403, title: "Outside the agent's reach" },
  approval_expired: { status: 409, title: "The approval expired before it was decided" },
  budget_exhausted: { status: 409, title: "A budget in the chain has no room for this" },
  // WALLET-P1: the company's prepaid credit cannot cover this (agents pause at $0, never debt)
  wallet_empty: { status: 409, title: "Not enough credit: add funds" },
  wallet_off: { status: 409, title: "The wallet is not switched on for this company" },
  // BILLING-PLANS: an AG over its DE's tier cap; billing not set up; a suspended company; Stripe
  ag_cap_reached: { status: 409, title: "This DE is at its tier's AG limit: upgrade the tier" },
  billing_not_configured: { status: 409, title: "Billing is not set up for this" },
  subscription_suspended: {
    status: 403,
    title: "The subscription is suspended: read-only until the invoice is paid",
  },
  payment_provider_error: { status: 502, title: "The payment provider refused or failed" },
  // Personal crews (M4-01; 04 §Auth model `X-CompanyOS-Identity-Mode`, 05 §Personal crews)
  identity_mode_refused: {
    status: 403,
    title: "The owner's autonomy policy does not allow acting in their name here",
  },
  delegation_loop: { status: 422, title: "Delegation too deep or back to an earlier principal" },
  worker_refused: {
    status: 422,
    title: "Admission refused the worker (no verbatim standing orders, depth or budget)",
  },
  // DE configuration (DC-01; ADR-0018, ADR-0019, ADR-0020 widened)
  version_not_draft: { status: 409, title: "A validated template version is immutable" },
  publish_refused: {
    status: 422,
    title: "Publish needs a validated version, a verification skill and every eval passing",
  },
  edit_eval_required: {
    status: 422,
    title: "An edit ships only with an eval that fails on based_on and passes on the new version",
  },
  deploy_state_conflict: { status: 409, title: "The deploy's state does not allow this" },
  deploy_already_open: { status: 409, title: "This DE already has an open deploy" },
  container_owned: {
    status: 409,
    title: "The company's Hermes container belongs to another digital employee",
  },
  container_not_imported: {
    status: 409,
    title: "The company's existing Hermes container must be imported by an operator first",
  },
  profile_name_conflict: {
    status: 409,
    title: "The digital employee's panel profile name is held by another digital employee",
  },
  bundle_refused: {
    status: 422,
    title: "The bundle failed validation (cap, checks, supervisor, budget or model map)",
  },
  verification_override_refused: {
    status: 422,
    title: "A verification override is for a pilot of a version without a verification skill",
  },
  signature_invalid: { status: 401, title: "Signature missing, invalid or outside its window" },
  verdict_already_recorded: { status: 409, title: "This run already has a verdict" },
  verdict_from_worker: { status: 403, title: "An ephemeral worker cannot record a verdict" },
  run_not_active: { status: 409, title: "The run is not running" },
  evidence_out_of_scope: { status: 422, title: "An object pointer is outside the run's prefix" },
  decision_seq_conflict: { status: 409, title: "Decision seq repeated or skipped" },
  premise_escalated: { status: 409, title: "This assumption was escalated; no third fix" },
  premise_not_shown: {
    status: 422,
    title: "A premise escalation needs two failed decisions sharing the assumption",
  },
  verdict_missing: { status: 422, title: "The run's template requires a verdict first" },
  verdict_not_verified: { status: 422, title: "A not_verified output never reaches an approver" },
  verdict_inconclusive: { status: 422, title: "An inconclusive output never reaches an approver" },
  credential_path_disabled: {
    status: 403,
    title: "This credential path is not enabled on the platform (founder decision pending)",
  },
  attestation_required: {
    status: 422,
    title: "The provider terms must be attested before the credential is used",
  },
  // BYO-01 tool connections
  connection_path_disabled: {
    status: 403,
    title: "Tool connections are not turned on for this company",
  },
  connection_not_set_up: {
    status: 409,
    title: "This provider needs admin setup (the OAuth app credentials) before anyone connects it",
  },
  connection_state_conflict: { status: 409, title: "This connection is disconnected" },
  // M4-04 connections: reconnect, refresh, mail and calendar for crews
  crew_connection_off: {
    status: 409,
    title: "The owner has not let their agent read this (mail or calendar)",
  },
  connection_unusable: {
    status: 409,
    title: "The owner's connection needs attention first (reconnect or check it)",
  },
  vendor_unavailable: {
    status: 502,
    title: "The connected service could not be reached or refused the call",
  },
  subscription_path_refused: {
    status: 403,
    title: "The subscription model path is not admitted for this run",
  },
  // Measurement and reports (13 §observation, §scorecard_publication, §Friday report)
  source_event_conflict: { status: 409, title: "Source event already recorded with other data" },
  metric_is_derived: { status: 422, title: "Metric is derived from events" },
  // AGENT-KPI-TOOL: `outcomes.record` on the agent's own seat only, in the metric's own terms
  metric_not_on_seat: { status: 403, title: "The metric is not on the agent's seat" },
  report_kind_mismatch: { status: 422, title: "The metric is not reported this way" },
  unit_mismatch: { status: 422, title: "The unit differs from the metric's" },
  // AGENT-REPORTING: a duty with `evidence_required` (13 §Reporting duties)
  evidence_required: { status: 422, title: "This report needs evidence" },
  already_published: { status: 409, title: "Period already published" },
  report_stale: { status: 422, title: "Report data changed since generation" },
  delivery_in_progress: {
    status: 409,
    title: "The delivery may still be submitting; reconcile after its submit deadline",
  },
  // Crew features (M4-03)
  // COS-SLICE1 (delegation guardrails): an agent run past its effects cap; a task parked after
  // three agent handoffs in 24 h (a person must touch it first); a card already decided.
  effect_cap_reached: {
    status: 409,
    title: "This run has made as many task creations and handoffs as its seat allows",
  },
  handoff_parked: { status: 409, title: "This task waits on a person after three handoffs" },
  // COS-MODES: an agent may not do this to another person's agent (configuration, a worker
  // under a lead agent, the urgent cap); the detail says what to do instead.
  direction_refused: { status: 403, title: "Agents may not direct this agent that way" },
  suggestion_decided: { status: 409, title: "This suggestion was already accepted or dismissed" },
  crew_paused: { status: 409, title: "Your agent is paused or deactivated" },
  triage_off: { status: 409, title: "Inbox triage is off for this agent" },
  autonomy_never: { status: 403, title: "The owner does not allow this action" },
  no_seat: { status: 422, title: "The owner holds no seat to attach this to" },
  grant_not_allowed: { status: 422, title: "That memory is not the owner's to grant" },
  // STEP1-OUTBOX: an outward act through a connection its requester holds no grant on (or one
  // past the grant's limits); an outbox item asked to retry or cancel in a state that cannot
  connection_grant_missing: {
    status: 403,
    title: "No grant lets this act go through that connection",
  },
  outbox_state_conflict: { status: 409, title: "Not possible in the outbox item's state" },
  // INC2-INTAKE: a dead letter asked to retry or discard in a state that cannot
  integration_job_state_conflict: { status: 409, title: "Not possible in the job's state" },
  // INC2-CONTACT: an identity (email, phone, an outside system's id) that belongs to another
  // contact; an agent asked to opt a contact back in after an opt-out (only a person may).
  contact_identity_taken: { status: 409, title: "That identity belongs to another contact" },
  // INC2-ACCESS: "act directly" asked for before the `external` class is enforced on every path
  act_mode_unavailable: { status: 422, title: "Acting directly is not available yet" },
  consent_opt_out_locked: {
    status: 403,
    title: "An opt-out is changed only by a person",
  },
  // HARDEN-pwa: the server has no VAPID keys (`push.config` says why)
  push_disabled: { status: 409, title: "Web push is not configured on this server" },
  // M8-ASSESS: a questionnaire link past its expiry or after its round closed; a round or
  // respondent change its state does not allow (open a draft only, close an open round, remind
  // a respondent who has not answered and is not past the reminder cap).
  assessment_link_expired: { status: 410, title: "This questionnaire link has expired" },
  assessment_state_conflict: { status: 409, title: "Not possible in the round's current state" },
  // Parity P6a (hybrid design §8.0, §8.1): an agent action the policy denies (a hard prohibition,
  // a class set to Never, a fence; never approvable), and an approved proposal whose target
  // changed since it was filed. Both carry `refusal` (contracts/approvals.ts `AgentRefusal`).
  policy_denied: { status: 403, title: "The agent policy does not allow this action" },
  target_changed: { status: 409, title: "The target changed since this was proposed" },
  // Parity P1 (design §1.3): an undo record past its 15 minutes, a subject changed since the
  // write (`detail` names who changed what: "Sam changed the status after this"), or a caller
  // who did not make the write.
  undo_expired: { status: 410, title: "This change can no longer be undone" },
  undo_stale: { status: 409, title: "The subject changed after this; it was not undone" },
  undo_not_allowed: { status: 403, title: "Only who made the change can undo it" },
  // WEBHOOKS-IN (design §2.2): an incoming webhook a person paused (senders retry after
  // `Retry-After`), or disabled (senders stop); a body that is not JSON.
  hook_paused: { status: 503, title: "This webhook is paused; retry later" },
  hook_disabled: { status: 410, title: "This webhook is disabled" },
  unsupported_media_type: { status: 415, title: "Send application/json" },
  // Parity P1 wave 1 (design §3.1): completing a task while a field required on completion is
  // empty; `missing` lists the field ids (`complete_task` answers the same code).
  required_missing: { status: 422, title: "Required fields are empty" },
  // AGENT-SAFETY: external content withheld from an agent run because it carries
  // instruction-shaped text; only in enforce mode, which the install must allow (off by default)
  content_quarantined: {
    status: 422,
    title: "This content was withheld because it carries instruction-shaped text",
  },
  // D15 (preview): a run used its memory write budget; nothing more is written this run
  memory_budget_exhausted: {
    status: 409,
    title: "This run has used its memory write budget",
  },
  // D14+D15: the seat's queue of memories waiting for Recall is full (Recall down or holding
  // them); nothing is written and the budget is not used
  memory_queue_full: {
    status: 409,
    title: "Too many memories are waiting for Recall",
  },
  // AGENT-PLUMBING: an agent-reported cost in another currency than the company's (no FX)
  currency_mismatch: { status: 422, title: "Not the company's reporting currency" },
  // AGENT-PLUMBING: a DE holds at most this many pending wake timers
  too_many_timers: { status: 409, title: "Too many pending wake timers" },
  // HANDOFF-NO-DROPS: a wake-up on a task that is done (or gone): no run is started on it
  subject_closed: { status: 409, title: "The wake-up's subject is closed" },
  // B5-DECIDE: neither Jev (switch off, or no OpenRouter key) nor the local model is available,
  // or both failed; the DE reads the items itself
  classifier_unavailable: { status: 503, title: "No classifier is available right now" },
  // TASK-LOCK: an agent run is working on the task; stop it first to move or reassign the task
  task_locked: { status: 409, title: "An agent is working on this task" },
  // BOARD-GATES: an agent's move out of a column only a person moves out of (never proposed;
  // `refusal.data.wait_on` names the event that wakes it)
  column_locked: { status: 403, title: "A person moves cards out of this column" },
  // MANAGED-POLICY: a managed company's people request agents; its agency adds them
  agent_mode_managed: {
    status: 403,
    title: "This company's agency adds agents: send a request instead",
  },
  // ONBOARDING-P3 (PLAN-DES): the company's plan includes this many DEs and all are in use
  de_allowance_reached: {
    status: 409,
    title: "The plan's digital employees are all in use: upgrade the plan to add one",
  },
  // lane A (beta shared contracts §3.2): an onboarding DE goes live only once set up and connected
  not_provisioned: {
    status: 409,
    title: "This digital employee is not set up and connected yet",
  },
  // TEMPLATES-T4: a role's required connection is neither connected nor marked "connect later";
  // a probation ended before its days and tasks are met
  connections_missing: { status: 422, title: "A required connection is not connected" },
  probation_not_met: { status: 409, title: "The probation's days or tasks are not met yet" },
  // SCREENS-S1 (docs/design/2026-10-28-screens-stack-contract.md)
  screens_no_stack: { status: 409, title: "This company has no screens stack yet" },
  screens_no_grant: { status: 403, title: "No grant on this account" },
  // SAVED-LOGIN-SHARING: several granted logins match the site or name: the agent names one.
  screens_pick_account: { status: 409, title: "Several saved logins match: name one" },
  screens_account_needs_you: { status: 409, title: "This account needs its owner to log in" },
  screens_lease_state: { status: 409, title: "The screen's state does not allow this" },
  screens_controlled: { status: 409, title: "Someone else has the screen" },
  screens_stack_failed: { status: 502, title: "The screens stack did not answer" },
  // The company's stack has no slots of that kind (`<kind>s_max` is 0): refused, never queued.
  screens_kind_unavailable: { status: 409, title: "This company's computer has none of that kind" },
} as const satisfies Record<string, { status: number; title: string }>;

export type ProblemCode = keyof typeof problemCatalog;
export const ProblemCode = z.enum(Object.keys(problemCatalog) as [ProblemCode, ...ProblemCode[]]);

export const ProblemFieldError = z
  .object({
    path: z.string().describe("Dotted path of the offending field (`items.3.occurred_at`)"),
    code: z.string(),
    message: z.string(),
  })
  .meta({ id: "ProblemFieldError" });

const base = {
  type: z.url(),
  title: z.string(),
  status: z.int().min(400).max(599),
  detail: z.string().optional(),
  instance: z.string().optional(),
  request_id: z.string(),
  errors: z.array(ProblemFieldError).optional(),
  current: z.unknown().optional().describe("409 `conflict`: the current resource"),
  // PAUSE-NO-DROP: 409 `budget_exhausted` at checkout — the named wake-ups are held, not dropped.
  held: z
    .object({ reason: z.string(), wakeup_ids: z.array(z.string()), note: z.string() })
    .optional()
    .describe("409 `budget_exhausted`: the wake-ups held until the agent is resumed"),
  // Parity P6a: a structured refusal (contracts/approvals.ts `AgentRefusal`, kept loose here so
  // the problem contract depends on nothing but enums and primitives).
  refusal: z
    .object({
      code: z.string(),
      message: z.string(),
      data: z.record(z.string(), z.unknown()).optional(),
    })
    .optional()
    .describe("A structured refusal (`AgentRefusal`): its code, sentence and data"),
};

// Extension members (RFC 9457 §3.2) required per code (ADR-0016 §4, §8). Each such code is its
// own variant, so the requirement is in the type, in `Problem.parse` and in the OpenAPI
// `oneOf` (finding #9).
const Feature = FeatureKey;
const variants = {
  feature_disabled: { feature: Feature },
  feature_read_only: { feature: Feature, read_only_until: IsoDate.nullable() },
  addon_requires: { feature: Feature, requires: z.array(AddonKey).min(1) },
  addon_required_by: { feature: Feature, required_by: z.array(AddonKey).min(1) },
  rate_limited: {
    retry_after: z.int().min(0).describe("seconds, also sent as `Retry-After`"),
  },
  // ONBOARD: why a token that matched its invitation can't be used (sent only to a caller who
  // presented the invitation's own secret; a forged or unknown token gets no reason).
  invitation_invalid: {
    reason: z
      .enum(["expired", "revoked", "accepted", "other_address"])
      .optional()
      .describe(
        "`expired` past its 14 days; `revoked` withdrawn or replaced by a newer invitation; `accepted` already used; `other_address` sent to another email than the signed-in account's",
      ),
  },
  // Parity P1 wave 1 (design §3.1): the empty fields that hold a task off a done status.
  required_missing: {
    missing: z
      .array(Uuid)
      .describe("The required fields left empty that the caller's field audience reads (ids)"),
    hidden: z
      .int()
      .min(1)
      .optional()
      .describe("How many more empty required fields the caller cannot see (never named)"),
  },
  // D15 (preview): the budget and what the run has used of it (do not retry this run)
  memory_budget_exhausted: {
    budget: z.int().min(0).describe("memory writes a run may make (the company's setting)"),
    used: z.int().min(0).describe("memory writes this run has made"),
  },
  // REACH-S1: which agent refused, and whom to ask for access (the people it serves)
  not_in_reach: {
    agent_membership_id: Uuid.describe("The agent outside whose reach the caller is"),
    ask: z
      .array(z.object({ membership_id: Uuid, name: z.string() }))
      .describe("Whom an access request goes to (empty: the company's owners and admins)"),
  },
  // BILLING-PLANS: the DE's tier, its AG cap and the tier that allows more (null at the top)
  ag_cap_reached: {
    tier: MaintenanceTier,
    ag_cap: z.int().min(0),
    next_tier: MaintenanceTier.nullable(),
  },
} as const;

type ExtendedCode = keyof typeof variants;
const plainCodes = (Object.keys(problemCatalog) as ProblemCode[]).filter(
  (c): c is Exclude<ProblemCode, ExtendedCode> => !(c in variants),
);

const variant = <C extends ExtendedCode>(code: C) =>
  z.object({ ...base, code: z.literal(code), ...variants[code] });

export const Problem = z
  .discriminatedUnion("code", [
    z.object({
      ...base,
      code: z.enum(
        plainCodes as [Exclude<ProblemCode, ExtendedCode>, ...Exclude<ProblemCode, ExtendedCode>[]],
      ),
    }),
    variant("feature_disabled"),
    variant("feature_read_only"),
    variant("addon_requires"),
    variant("addon_required_by"),
    variant("rate_limited"),
    variant("invitation_invalid"),
    variant("required_missing"),
    variant("memory_budget_exhausted"),
    variant("not_in_reach"),
    variant("ag_cap_reached"),
  ])
  .superRefine((p, ctx) => {
    const spec = problemCatalog[p.code];
    if (p.status !== spec.status)
      ctx.addIssue({ code: "custom", path: ["status"], message: `${p.code} is ${spec.status}` });
    if (p.type !== `${PROBLEM_TYPE_BASE}${p.code}`)
      ctx.addIssue({ code: "custom", path: ["type"], message: "type must match code" });
  })
  .meta({ id: "Problem" });
export type Problem = z.infer<typeof Problem>;
export type ProblemOf<C extends ProblemCode> = Problem & { code: C };

/** Extension members a code requires (for docs and the API's error mapper). */
export const problemExtensions: Partial<Record<ProblemCode, readonly string[]>> =
  Object.fromEntries(Object.entries(variants).map(([c, v]) => [c, Object.keys(v)]));

type Generated = "type" | "title" | "status" | "code" | "request_id";
export type ProblemExtras<C extends ProblemCode> = Omit<ProblemOf<C>, Generated>;

/**
 * Builds a problem for `code` with the catalog's status, title and type. The required extension
 * members of `code` are required arguments, and the result is validated (never an invalid
 * `Problem`).
 */
export function problem<C extends ProblemCode>(
  code: C,
  requestId: string,
  ...extra: Partial<ProblemExtras<C>> extends ProblemExtras<C>
    ? [ProblemExtras<C>?]
    : [ProblemExtras<C>]
): ProblemOf<C> {
  const spec = problemCatalog[code];
  return Problem.parse({
    type: `${PROBLEM_TYPE_BASE}${code}`,
    title: spec.title,
    status: spec.status,
    code,
    request_id: requestId,
    ...(extra[0] ?? {}),
  }) as ProblemOf<C>;
}
